/**
 * storyFieldsApi — the story's field picker (`/api/v2/stories/:storyId/fields`)
 * and a field's value list (`/api/v2/models/:rel/fields/:id/values`).
 *
 * Shares REACT_APP_STORY_FILTERS_API with the filters client: the picker's
 * `active_filter_id` only makes sense against the same filter store.
 */
import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import { mockFetchFieldValues } from "./fieldValues.mock";
import { mockFetchStoryFields } from "./storyFields.mock";
import type {
  FieldValuesParams,
  FieldValuesResponseApi,
  StoryFieldListParams,
  StoryFieldListResponseApi,
} from "./storyFilters.types";

const isMockStoryFields = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_FILTERS_API);

/** Hand-built for the same repeated `kind=a&kind=b` reason as `storyFiltersApi`. Unset params are left out. */
const withQuery = (
  path: string,
  { canvasItemId, kinds, usedOnly, includeIdentifiers, search }: StoryFieldListParams,
): string => {
  const query = new URLSearchParams();
  if (canvasItemId) query.append("canvas_item_id", canvasItemId);
  kinds?.forEach((kind) => query.append("kind", kind));
  if (usedOnly) query.append("used_only", "true");
  if (includeIdentifiers) query.append("include_identifiers", "true");
  const text = search?.trim();
  if (text) query.append("search", text);
  const serialised = query.toString();
  return serialised ? `${path}?${serialised}` : path;
};

/** Fields the filter pickers can offer, already narrowed by the server. */
export async function fetchStoryFields(
  storyId: string,
  params: StoryFieldListParams = {},
): Promise<StoryFieldListResponseApi> {
  if (isMockStoryFields()) return mockFetchStoryFields(storyId, params);

  return apiService.get<StoryFieldListResponseApi>(
    withQuery(API_ENDPOINTS.stories.fields(storyId), params),
  );
}

/** Unset params are left out; `limit` and `offset` are sent as given, `0` included. */
const withValuesQuery = (
  path: string,
  { role, storyId, canvasItemId, search, limit, offset }: FieldValuesParams,
): string => {
  const query = new URLSearchParams();
  if (role) query.append("role", role);
  if (storyId) query.append("story_id", storyId);
  if (canvasItemId) query.append("canvas_item_id", canvasItemId);
  const text = search?.trim();
  if (text) query.append("search", text);
  if (limit !== undefined) query.append("limit", String(limit));
  if (offset !== undefined) query.append("offset", String(offset));
  const serialised = query.toString();
  return serialised ? `${path}?${serialised}` : path;
};

/**
 * One page of a field's distinct values. `modelReleaseId` and `fieldId` come
 * from the picker's field (`model_release_id`, `field.model_field_id`).
 */
export async function fetchFieldValues(
  modelReleaseId: string,
  fieldId: string,
  params: FieldValuesParams = {},
): Promise<FieldValuesResponseApi> {
  if (isMockStoryFields()) return mockFetchFieldValues(modelReleaseId, fieldId, params);

  return apiService.get<FieldValuesResponseApi>(
    withValuesQuery(API_ENDPOINTS.dataModels.fieldValues(modelReleaseId, fieldId), params),
  );
}
